import { useParams, Link, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useSEO } from '@/hooks/useSEO';
import PageNavbar from "@/components/feature/PageNavbar";
import Footer from "@/pages/home/components/Footer";
import { getServiceBySlug, servicesData } from "@/pages/services/data";

const SITE_URL = (import.meta.env.VITE_SITE_URL as string) || 'https://isman.co.ke';

export default function ServiceDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const service = getServiceBySlug(slug ?? "");
  const [lightbox, setLightbox] = useState<number | null>(null);

  const serviceSchema = useMemo(() => {
    if (!service) return undefined;
    return [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": service.title,
        "description": service.overview,
        "url": SITE_URL + "/services/" + service.slug,
        "provider": {
          "@type": "LocalBusiness",
          "name": "ISMAN Engineering Company",
          "url": SITE_URL,
          "telephone": "+254724114555",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Nairobi",
            "addressCountry": "KE"
          }
        },
        "areaServed": { "@type": "Country", "name": "Kenya" },
        "serviceType": service.shortTitle
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
          { "@type": "ListItem", "position": 2, "name": "Services", "item": SITE_URL + "/services" },
          { "@type": "ListItem", "position": 3, "name": service.shortTitle, "item": SITE_URL + "/services/" + service.slug }
        ]
      }
    ];
  }, [service]);

  useSEO({
    title: service ? service.title + " | ISMAN Engineering Kenya" : "Service | ISMAN Engineering Kenya",
    description: service ? service.tagline + ". " + service.overview.slice(0, 120) + "..." : "",
    keywords: service ? service.shortTitle + " Kenya, " + service.applications.slice(0, 3).join(", ") : "",
    canonical: service ? SITE_URL + "/services/" + service.slug : SITE_URL + "/services",
    schema: serviceSchema,
  });

  useEffect(() => {
    if (!service) navigate("/services", { replace: true });
    window.scrollTo(0, 0);
  }, [slug, service, navigate]);

  if (!service) return null;

  return (
    <div className="min-h-screen bg-white">
      <PageNavbar />
      <main className="pt-8">

        {/* ── Hero ── */}
        <section className="relative w-full h-[65vh] min-h-[480px] overflow-hidden">
          <img
            src={service.image}
            alt={service.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-950/90 via-cyan-900/75 to-teal-900/50"></div>
          <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${service.accentFrom} ${service.accentTo}`}></div>

          {/* Breadcrumb */}
          <div className="absolute top-8 left-6 md:left-16 flex items-center gap-2 text-white/60 text-xs">
            <Link to="/" className="hover:text-cyan-300 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/services" className="hover:text-cyan-300 transition-colors">Services</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-cyan-300 font-medium">{service.shortTitle}</span>
          </div>

          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-6 md:px-16 w-full">
              {/* Icon */}
              <div className={`w-14 h-14 flex items-center justify-center rounded-xl bg-gradient-to-br ${service.accentFrom} ${service.accentTo} mb-5`}>
                <i className={`${service.icon} text-white text-2xl`}></i>
              </div>
              <span className="text-cyan-300 text-xs font-semibold uppercase tracking-widest mb-3 block">
                {service.subtitle}
              </span>
              <h1 className="text-white text-3xl md:text-5xl font-extrabold leading-tight max-w-3xl mb-4">
                {service.title}
              </h1>
              <p className="text-white/70 text-sm md:text-base leading-relaxed max-w-xl mb-8">
                {service.tagline}
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="#scope"
                  onClick={(e) => { e.preventDefault(); document.querySelector("#scope")?.scrollIntoView({ behavior: "smooth" }); }}
                  className="bg-cyan-600 hover:bg-cyan-500 text-white font-bold px-7 py-2.5 rounded-md uppercase text-sm tracking-wider transition-colors cursor-pointer whitespace-nowrap"
                >
                  See Scope of Work
                </a>
                <Link
                  to="/#contact"
                  className="border border-white/40 hover:border-cyan-400 hover:text-cyan-300 text-white font-bold px-7 py-2.5 rounded-md uppercase text-sm tracking-wider transition-colors whitespace-nowrap"
                >
                  Request a Quote
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ── Overview ── */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-14 items-start">
              {/* Text — 3 cols */}
              <div className="lg:col-span-3">
                <span className="text-cyan-600 text-xs font-semibold uppercase tracking-widest">Service Overview</span>
                <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mt-3 mb-6 leading-tight">
                  What ISMAN Delivers in {service.shortTitle}
                </h2>
                <p className="text-gray-600 text-sm leading-relaxed mb-5">{service.overview}</p>
                <p className="text-gray-600 text-sm leading-relaxed">{service.detail}</p>

                {/* Applications */}
                <div className="mt-8">
                  <p className="text-gray-800 font-semibold text-sm uppercase tracking-wide mb-4">Industries We Serve</p>
                  <div className="flex flex-wrap gap-2">
                    {service.applications.map((app) => (
                      <span key={app} className="bg-cyan-50 text-cyan-700 border border-cyan-100 text-xs font-medium px-3 py-1.5 rounded-full">
                        {app}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Benefits panel — 2 cols */}
              <div className="lg:col-span-2">
                <div className="bg-gray-50 rounded-2xl p-8 border border-gray-100">
                  <div className="flex items-center gap-3 mb-6">
                    <div className={`w-10 h-10 flex items-center justify-center rounded-full bg-gradient-to-br ${service.accentFrom} ${service.accentTo} shrink-0`}>
                      <i className="ri-award-line text-white text-base"></i>
                    </div>
                    <div>
                      <p className="text-gray-900 font-bold text-sm">Why Choose ISMAN?</p>
                      <p className="text-gray-500 text-xs">{service.shortTitle} Specialists</p>
                    </div>
                  </div>
                  <ul className="space-y-4">
                    {service.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-gray-600">
                        <span className="w-5 h-5 flex items-center justify-center bg-cyan-100 rounded-full mt-0.5 shrink-0">
                          <i className="ri-check-line text-cyan-600 text-xs"></i>
                        </span>
                        {benefit}
                      </li>
                    ))}
                  </ul>

                  {/* CTA in panel */}
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <p className="text-gray-800 font-semibold text-sm mb-3">Ready to get started?</p>
                    <a
                      href="tel:0724114555"
                      className="w-full bg-cyan-600 hover:bg-cyan-700 text-white font-bold px-5 py-3 rounded-md text-sm uppercase tracking-wider transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                    >
                      <i className="ri-phone-line"></i> Call: 072 411 4555
                    </a>
                    <a
                      href="mailto:info@isman.co.ke"
                      className="w-full mt-2 border border-gray-200 hover:border-cyan-300 text-gray-600 hover:text-cyan-700 font-semibold px-5 py-3 rounded-md text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                    >
                      <i className="ri-mail-line"></i> info@isman.co.ke
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Scope of Work ── */}
        <section id="scope" className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="inline-block bg-cyan-50 text-cyan-600 text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full">
                Scope of Work
              </span>
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mt-4">
                Everything Included in This Service
              </h2>
              <p className="text-gray-500 text-sm mt-3 max-w-lg mx-auto">
                Our {service.shortTitle.toLowerCase()} service covers all aspects of the work — from initial assessment to final handover.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {service.scope.map((item, i) => (
                <div
                  key={i}
                  className="bg-white rounded-xl border border-gray-100 hover:border-cyan-200 group transition-all overflow-hidden flex flex-col"
                >
                  {item.image && (
                    <figure className="relative aspect-[16/10] overflow-hidden bg-gray-100">
                      <img
                        src={item.image}
                        alt={item.caption || item.title}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      {item.caption && (
                        <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent px-4 pb-3 pt-8 text-white text-[11px] font-medium leading-snug">
                          {item.caption}
                        </figcaption>
                      )}
                    </figure>
                  )}
                  <div className="p-6">
                  <div className={`w-11 h-11 flex items-center justify-center rounded-xl bg-gradient-to-br ${service.accentFrom} ${service.accentTo} mb-4 group-hover:scale-105 transition-transform`}>
                    <i className={`${item.icon} text-white text-lg`}></i>
                  </div>
                  <h3 className="text-gray-900 font-bold text-sm mb-2">{item.title}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Project Gallery ── */}
        {service.gallery && service.gallery.length > 0 && (
          <section id="gallery" className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center mb-12">
                <span className="inline-block bg-cyan-50 text-cyan-600 text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full">
                  Gallery
                </span>
                <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mt-4">
                  {service.shortTitle} We Install
                </h2>
                <p className="text-gray-500 text-sm mt-3 max-w-lg mx-auto">
                  Mills, conveyors, silos, tanks and processing lines — built in food-grade stainless steel.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {service.gallery.map((g, i) => (
                  <button
                    key={g.src}
                    type="button"
                    onClick={() => setLightbox(i)}
                    className="group relative overflow-hidden rounded-xl bg-gray-100 aspect-[4/3] text-left cursor-pointer"
                  >
                    <img
                      src={g.src}
                      alt={g.caption}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent p-4 pt-10">
                      <p className="text-white text-xs font-medium leading-snug">{g.caption}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </section>
        )}

        {lightbox !== null && service.gallery && (
          <div
            className="fixed inset-0 z-[90] bg-black/90 flex items-center justify-center p-4"
            onClick={() => setLightbox(null)}
            role="dialog"
            aria-modal="true"
          >
            <button className="absolute top-4 right-5 text-white/80 hover:text-white text-3xl cursor-pointer" aria-label="Close" onClick={() => setLightbox(null)}>
              <i className="ri-close-line"></i>
            </button>
            <button
              className="absolute left-3 md:left-6 text-white/80 hover:text-white text-4xl cursor-pointer"
              aria-label="Previous"
              onClick={(e) => { e.stopPropagation(); setLightbox((lightbox - 1 + service.gallery!.length) % service.gallery!.length); }}
            >
              <i className="ri-arrow-left-s-line"></i>
            </button>
            <figure className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
              <img src={service.gallery[lightbox].src} alt={service.gallery[lightbox].caption} className="w-full max-h-[80vh] object-contain rounded-lg" />
              <figcaption className="text-white/80 text-sm text-center mt-3">{service.gallery[lightbox].caption}</figcaption>
            </figure>
            <button
              className="absolute right-3 md:right-6 text-white/80 hover:text-white text-4xl cursor-pointer"
              aria-label="Next"
              onClick={(e) => { e.stopPropagation(); setLightbox((lightbox + 1) % service.gallery!.length); }}
            >
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        )}

        {/* ── Related Services ── */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl font-extrabold text-gray-900">Explore Other Services</h2>
              <p className="text-gray-500 text-sm mt-2">ISMAN COMPANY provides a full suite of industrial engineering solutions</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {servicesData
                .filter((s) => s.slug !== service.slug)
                .map((s) => (
                  <Link
                    key={s.slug}
                    to={`/services/${s.slug}`}
                    className="group flex flex-col items-center gap-3 bg-gray-50 hover:bg-cyan-50 border border-gray-100 hover:border-cyan-200 rounded-xl p-4 transition-all text-center cursor-pointer"
                  >
                    <div className={`w-10 h-10 flex items-center justify-center rounded-full bg-gradient-to-br ${s.accentFrom} ${s.accentTo}`}>
                      <i className={`${s.icon} text-white text-base`}></i>
                    </div>
                    <p className="text-gray-700 group-hover:text-cyan-700 font-semibold text-xs leading-snug transition-colors">
                      {s.shortTitle}
                    </p>
                  </Link>
                ))}
            </div>
          </div>
        </section>

        {/* ── Final CTA ── */}
        <section className="py-16 bg-gradient-to-r from-cyan-700 to-teal-700">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-white text-3xl font-extrabold mb-4">
              Ready to Work With ISMAN COMPANY?
            </h2>
            <p className="text-cyan-100 text-sm mb-8 leading-relaxed max-w-xl mx-auto">
              Contact us today for a free, no-obligation site assessment and quotation.
              Our engineers are ready to help you find the best solution for your facility.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:0724114555"
                className="bg-white text-cyan-700 hover:bg-cyan-50 font-bold px-8 py-3.5 rounded-md uppercase text-sm tracking-wider transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-phone-line"></i> 072 411 4555
              </a>
              <a
                href="mailto:info@isman.co.ke"
                className="border-2 border-white/60 hover:border-white text-white font-bold px-8 py-3.5 rounded-md uppercase text-sm tracking-wider transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-mail-line"></i> info@isman.co.ke
              </a>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}
